
/**
 * Core Web Vitals monitoring functionality
 */
import { ReportHandler } from './types';
import { registerObservers } from './observers';
import { collectFCP, collectPostLoadMetrics } from './metrics';
import { generateMetricId } from './utils';

// Store the reporting function for later use
let reportFunction: ReportHandler | null = null;

/**
 * Initialize Web Vitals monitoring
 * @param onPerfEntry Optional callback function to handle performance metrics
 */
export function initWebVitals(onPerfEntry?: ReportHandler) {
  reportFunction = onPerfEntry || defaultReportHandler;
  
  if (typeof window !== 'undefined') {
    // Register performance observers to track metrics
    registerObservers();
    
    // Collect first contentful paint metric 
    collectFCP();
    
    // Queue collection of other metrics after page load
    if (document.readyState === 'complete') {
      collectPostLoadMetrics();
    } else {
      window.addEventListener('load', collectPostLoadMetrics);
    }
  }
}

/**
 * Default report handler that logs to console in development
 * @param metric The metric to report
 */
function defaultReportHandler(metric: { name: string; value: number; delta: number }) {
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}: ${Math.round(metric.value)}${metric.name === 'CLS' ? '' : 'ms'}`);
  }
}

/**
 * Report a metric value
 * @param name The metric name
 * @param value The metric value
 * @param data Additional data to include
 */
export function reportMetric(name: string, value: number, data: Record<string, any> = {}) {
  try {
    if (reportFunction) {
      reportFunction({ 
        name, 
        value, 
        delta: value, 
        id: generateMetricId(name), 
        entries: [], 
        ...data 
      });
    }
  } catch (e) {
    console.error('Error reporting metric:', e);
  }
}

/**
 * Report a route change event
 * @param route The route path
 */
export function reportRouteChange(route: string) {
  try {
    if ('performance' in window) {
      performance.mark(`route-enter-${route}`);
      reportMetric('RouteChange', 0, { route });
    }
  } catch (e) {
    console.error('Error reporting route change:', e);
  }
}

/**
 * Measure the execution time of an asynchronous function
 * @param fn The async function to measure
 * @param label A label for the measurement
 * @returns The result of the function
 */
export async function measureAsyncExecutionTime<T>(
  fn: () => Promise<T>,
  label: string
): Promise<T> {
  const startTime = performance.now();
  try {
    return await fn();
  } finally {
    const endTime = performance.now();
    const duration = endTime - startTime;
    if (import.meta.env.DEV) {
      console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
    }
    
    // Report as custom metric
    reportMetric('CustomTiming', duration, { label });
  }
}
